import { AnimatePresence, motion } from 'framer-motion'
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  FaCalendarAlt,
  FaHome,
  FaSignOutAlt,
  FaTasks,
  FaTimes,
} from 'react-icons/fa'
import { Link, useLocation } from 'react-router-dom'
import { useIsMobile } from '../../hooks'
import { useAuthStore } from '../../store/auth.store'
import { cn } from '../../utils/cn'
import './Sidebar.css'

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation()
  const { t } = useTranslation()
  const { user, logout } = useAuthStore()
  const isMobile = useIsMobile()

  const handleLogout = () => {
    logout()
    if (isMobile) {
      onClose()
    }
  }

  const handleLinkClick = () => {
    if (isMobile) {
      onClose()
    }
  }

  // Navigation items array for better organization
  const navItems = [
    {
      path: '/workspaces',
      icon: <FaHome size={20} />,
      text: t('workspace.title'),
    },
    {
      path: '/calendar',
      icon: <FaCalendarAlt size={20} />,
      text: t('calendar.title'),
    },
    {
      path: '/my-tasks',
      icon: <FaTasks size={20} />,
      text: t('myTasks.title'),
    },
  ]

  return (
    <>
      <AnimatePresence>
        {isMobile && isOpen && (
          <motion.div
            className="sidebar__backdrop"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          />
        )}
      </AnimatePresence>

      <aside className={cn('sidebar', isOpen && 'sidebar--open')}>
        <div className="sidebar__header">
          <div className="sidebar__header_nav">
            <img
              src="/assets/images/logo.svg"
              alt="logo"
              className="sidebar__logo"
            />
            <h2 className="sidebar__title">Ijro tizimi</h2>
          </div>

          {isMobile && (
            <motion.button
              className="sidebar__close"
              onClick={onClose}
              aria-label="Close sidebar"
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
            >
              <FaTimes size={20} />
            </motion.button>
          )}
        </div>

        <nav className="sidebar__nav">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                'sidebar__link',
                location.pathname.startsWith(item.path) &&
                  'sidebar__link--active'
              )}
              onClick={handleLinkClick}
            >
              <span className="sidebar__link-icon">{item.icon}</span>
              <span className="sidebar__link-text">{item.text}</span>
            </Link>
          ))}
        </nav>

        <div className="sidebar__footer">
          {user && (
            <div className="sidebar__user">
              <Link to="/profile">
                <motion.div
                  className="sidebar__user-info"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <div className="sidebar__user-avatar">
                    {user.name?.charAt(0).toUpperCase() ||
                      user.email?.charAt(0).toUpperCase()}
                  </div>
                  <div className="sidebar__user-details">
                    <div className="sidebar__user-name">{user.name}</div>
                    <div className="sidebar__user-email">{user.email}</div>
                  </div>
                </motion.div>
              </Link>
              <motion.button
                className="sidebar__logout"
                onClick={handleLogout}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <FaSignOutAlt size={16} />
                Chiqish
              </motion.button>
            </div>
          )}
        </div>
      </aside>
    </>
  )
}
